
import { Save } from 'lucide-react';

import { featureHook } from '@features/feature-hook';
import { getKitchenRenderSignature } from '@helpers';
import { lang } from '@lang';
import { repointDetachedDraft } from './helpers';
import { saveCustomerBlueprint } from '@external';
import { useKeystoneSession } from '../../auth/use-keystone-session';
import { useAppToast, useDesign } from '@hooks';
import { useNavigate } from 'react-router-dom';

import { AppContext, DesignActionTypes } from '@context';
import { appMode, clearDirty, customerState, loadCustomer, selectAiChanged, selectAiRendering, selectCanonical, selectNavigation, selectRawChanged, selectRawRendering, selectRenderingDirty, selectSaveAsDraftClose, selectSignature, setIsSaving, setSaveAsDraftClose, useAppDispatch, useAppSelector } from '@state';
import { Feature, FeatureHoookParams } from '..';
import { useContext, useEffect, useRef, useState } from 'react';
import { ComponentController } from '@rta/rta-blueprint-component';

type UseSaveFeatureParams = FeatureHoookParams;
// eslint-disable-next-line max-lines-per-function
export const useSaveFeature = (params: UseSaveFeatureParams = {}): Feature<boolean> => {
	const [controller, design] = useDesign();
	const dispatch = useAppDispatch();
	const { getToken } = useKeystoneSession();
	const navigate = useNavigate();

	const appToast = useAppToast();
	const toastCtrlRef = useRef<ReturnType<typeof appToast.loading> | null>(null);
	// true while a successful save is waiting for its post-save navigation to land, so the app-lock releases only once the reload begins
	const pendingUnlockRef = useRef<boolean>(false);

	const [success, setSuccess] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	const customer = useAppSelector(customerState);
	const saveAsDraft = useAppSelector(selectSaveAsDraftClose);
	const rawRendering = useAppSelector(selectRawRendering);
	const aiRendering = useAppSelector(selectAiRendering);
	const rawChanged = useAppSelector(selectRawChanged);
	const aiChanged = useAppSelector(selectAiChanged);
	const canonical = useAppSelector(selectCanonical);
	const signature = useAppSelector(selectSignature);
	const renderingDirty = useAppSelector(selectRenderingDirty);
	// const autoSaveId = controller?.kitchen?.context?.blueprint?.id ?? '';

	const { appDispatch, appState: { customerDesigns: designs }} = useContext(AppContext);
	const appNav = useAppSelector(selectNavigation);

	const fnSuccess = (success: string, savedVersion?: number | null): void => {
		console.log('success', success, controller, savedVersion);
		if (controller && success !== null && appNav.email) {
			toastCtrlRef.current?.success({ title: lang.customer.message.saveSuccess });
			// clean up dirty flag and local storage
			// console.log('remove', autoSaveId);
			localStorage.removeItem(`autosave-${controller.blueprint.id}`);
			controller.kitchen.isLocal = false;

			appDispatch({ type: DesignActionTypes.setIsDirty, payload: { id: appNav.designId ?? '', isDirty: false }});

			// promoteDraft left the controllers holding a deleted draft — later edits/renames
			// would split between the detached kitchen and the blueprint (stale packages)
			repointDetachedDraft(design);

			// After save, navigate to reload the page with the version number
			// If we saved as a new version (promoted draft), the version will be incremented by backend
			// Try to get the version from: savedVersion param, controller.kitchenVersion, or blueprint's latest version
			let versionAfterSave: number | null | undefined = savedVersion;
			
			// If we promoted a draft, check if we have the version from the controller
			// The controller.kitchenVersion should be set after promoteDraft()
			if (versionAfterSave === null || versionAfterSave === undefined) {
				versionAfterSave = controller.kitchenVersion ?? null;
			}
			
			// If still no version, try to get it from the blueprint's latest version
			// This will be the version the backend saved (after incrementing)
			if (versionAfterSave === null || versionAfterSave === undefined) {
				const latestVersion = controller.blueprint?.kitchens?.getLatestVersion();
				versionAfterSave = latestVersion?.version ?? null;
			}
			
			// If we still don't have a version, increment from current appNav.designVersion
			// This handles the case where we're saving a draft as a new version
			if (versionAfterSave === null || versionAfterSave === undefined) {
				const currentVersion = appNav.designVersion ? Number(appNav.designVersion) : 0;
				versionAfterSave = currentVersion + 1;
			}
			
			// Always navigate with version number and ?draft parameter to keep editor editable
			// This ensures the page loads the correct version and creates a draft for editing
			navigate(`/r/${appNav.email}/${appNav.designId}/${versionAfterSave}?draft`);
		}
	};

	// useEffect(() => {
	// }, [success]);

	useEffect(() => {
		if (error !== null) {
			toastCtrlRef.current?.error({ title: lang.customer.message.saveFailed });
		}
	}, [error]);

	// Release the full-app save lock once the save-triggered navigation lands (the reload has begun).
	// Fires only on appNav change, so a save's own network wait keeps the lock up until the reload.
	useEffect(() => {
		if (!pendingUnlockRef.current) return;
		pendingUnlockRef.current = false;
		dispatch(setIsSaving(false));
	}, [appNav]);

	const currentDesign = designs.designs.find((bp) => bp.blueprint.id === appNav.designId) ?? null;
	const value = currentDesign ? currentDesign.isDirty : false;
	
	// Check if current version is a draft (version === null)
	const isDraft = controller?.kitchen?.version === null;
	// Check if controller is read-only
	const isReadOnly = controller?.readonly ?? false;
	// Check if version is saved (not draft) and has no changes
	const isSavedVersionWithoutChanges = controller?.kitchen?.version !== null && !value;

	const [disabled, tooltip] = (() => {
		if (!controller) return [true, lang.customer.tooltip.noController];
		if (isReadOnly) return [true, lang.customer.tooltip.readOnly];
		if (isSavedVersionWithoutChanges && !renderingDirty) return [true, lang.customer.tooltip.notDirty];
		
		// Enable save if:
		// 1. Has changes (isDirty), OR
		// 2. Is a draft (even without changes - allows promoting draft to new version)
		if (value) {
			// Has changes
			return isDraft 
				? [false, lang.customer.tooltip.saveDraftAsNewVersion]
				: [false, lang.customer.tooltip.saveDesign];
		}
		if (isDraft) {
			// Draft without changes - can still save as new version
			return [false, lang.customer.tooltip.saveDraftAsNewVersion];
		}
		if (renderingDirty) return [false, lang.customer.tooltip.saveDesign];

		// Should not reach here, but fallback
		return [true, lang.customer.tooltip.notDirty];
	})();


	return featureHook<boolean>({
		name: 'saveFeature',
		label: lang.customer.label.saveSubmission,
		shortcut: '',
		tooltip,
		icon: (props = {}) => <Save {...props} />,
		disabled,
		value,
		hidden: appNav.appMode === appMode.view,
		confirm: [
			// Always show confirmation dialog
			saveAsDraft,
			() => {
				dispatch(setSaveAsDraftClose({ isOpen: true }));
			}
		],
		reset: () => {
			dispatch(setSaveAsDraftClose({ isOpen: false, confirmed: null }));
		},
		handler: () => {
			if (controller && appNav.email) {
				const isDraft = controller.kitchen.version === null;
				
				// Check if user wants to save as draft
				const wantsToSaveAsDraft = saveAsDraft.confirmed === true;
				
				// If trying to save as draft but no changes, show error and return
				// (a rendering-only change still counts as something to save)
				if (wantsToSaveAsDraft && !value && !renderingDirty) {
					appToast.warning({ title: 'Cannot save as draft', description: 'No changes to save to draft' });
					return;
				}
				
				// Capture the base version before saving (for calculating new version after promotion)
				let baseVersionForNewVersion: number | null = null;
				
				// If it's a draft and user chose "New Version" (saveAsDraft.confirmed === false)
				// Then promote draft to new version
				// Note: Backend handles version increment - frontend just promotes the draft
				if (isDraft && !wantsToSaveAsDraft) {
					// Get the base version of the draft before promoting
					// The draft's baseVersion tells us what version it was based on
					const draftKitchen = controller.kitchen.context.blueprint?.kitchens?.findVersion(null);
					if (draftKitchen) {
						// The draft is based on a saved version - when we promote, the backend will save it as baseVersion + 1
						baseVersionForNewVersion = draftKitchen.baseVersion ?? null;
						
						// If we have appNav.designVersion, use that as the base (it should be the version we're editing)
						if (baseVersionForNewVersion === null && appNav.designVersion !== null) {
							baseVersionForNewVersion = Number(appNav.designVersion);
						}
					}
					
					// Promote draft to new version
					// The backend will increment the version when saving
						const kitchens = controller.kitchen.context.blueprint?.kitchens;
						if (kitchens) {
							// Promote draft but do not switch controller to the new saved version here.
							// Keep editing state (draft) until navigation reloads with ?draft.
							void kitchens.promoteDraft();
						}
				}
				
				// Lock the entire app for the whole save + reload so an export can't overlap the save-triggered remount
				dispatch(setIsSaving(true));
				pendingUnlockRef.current = true;

				// Show loading toast with screen block
				toastCtrlRef.current = appToast.loading({ title: lang.app.loading.saving });

				// Save (either as draft or as new version/saved version)
				// Only send raw/ai when freshly captured (base64) — a seeded URL must never be
				// re-sent, or the API would base64-decode a URL into a corrupt blob.
				// One image: send whichever variant is canonical as `raw` — the API stores it inline
				// on blueprint.thumbnail (no blob). null clears it.
				const chosenImage = canonical === 'ai' ? aiRendering : rawRendering;
				const chosenChanged = canonical === 'ai' ? aiChanged : rawChanged;
				const renderingsPayload = renderingDirty
					? {
						canonical,
						signature,
						...(chosenChanged ? { raw: chosenImage || null } : {})
					}
					: undefined;

				// Mirror the server's signature locally so staleness works this session without a
				// reload. Same hash of the same payload the server stores → guaranteed to match.
				const savedSignature = getKitchenRenderSignature(controller.kitchen);
				const savedMeta = controller.kitchen?.context?.blueprint?.metadata;
				if (savedMeta) {
					savedMeta.savedKitchenSignature = savedSignature;
					if (renderingsPayload) savedMeta.renderingKitchenSignature = savedSignature;
				}

				saveCustomerBlueprint({
					controller,
					customerEmail: appNav.email,
					mode: 'live',
					getToken,
					renderings: renderingsPayload,
					setSuccess: (success: string) => {
						setSuccess(success);
						if (renderingDirty) dispatch(clearDirty());

						// Calculate the version after save
						let versionAfterSave: number | null | undefined = null;

						// If we promoted a draft to new version, backend incremented it
						// The new version should be baseVersion + 1
						if (isDraft && !wantsToSaveAsDraft && baseVersionForNewVersion !== null) {
							versionAfterSave = baseVersionForNewVersion + 1;
						} else {
							// If we saved as draft or saved an existing version, use current version
							versionAfterSave = controller.kitchenVersion ?? controller.kitchen.version ?? appNav.designVersion;
						}

						fnSuccess(success, versionAfterSave);
					},
					setError: (error: string) => {
						// A failed save must not brick the app — unlock so the user can switch designs or export the in-memory state
						pendingUnlockRef.current = false;
						dispatch(setIsSaving(false));
						repointDetachedDraft(design);
						setError(error);
					}
				});
			}
		}
	});
};
